/**
 * @description Tour guiado do teste grátis (29/10): destaca cada parte do sistema (menu,
 *              troca de perfil, notificações, ajuda e a Celeri) com um recorte de luz e um
 *              cartão com o que ela faz. Passo sem alvo visível na tela é pulado.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Lucide } from '@/chassis/ui/components';
import { CeleriIcone } from '@/chassis/ui/components/brand/Celeri';

export interface PassoTour {
    /** Seletor do elemento destacado; sem ele, o cartão fica no meio da tela. */
    alvo?: string;
    titulo: string;
    texto: string;
}

const CARTAO_LARGURA = 340;
const FOLGA = 8;

function alvoVisivel(seletor: string): HTMLElement | null {
    const el = document.querySelector<HTMLElement>(seletor);
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0 || r.right <= 0 || r.left >= window.innerWidth) return null;
    return el;
}

type Caixa = { top: number; left: number; width: number; height: number };

/** Lugar do cartão: à direita do alvo (menu lateral), senão abaixo, senão acima, sempre na tela. */
function posicaoCartao(alvo: Caixa | null): { top: number; left: number } {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const largura = Math.min(CARTAO_LARGURA, vw - 32);
    if (!alvo) return { top: Math.max(16, vh / 2 - 130), left: Math.max(16, vw / 2 - largura / 2) };
    const cabe = (l: number) => Math.min(Math.max(16, l), vw - largura - 16);
    if (alvo.left + alvo.width + 16 + largura < vw && alvo.left < vw / 3) {
        return { top: Math.min(Math.max(16, alvo.top - 8), vh - 240), left: alvo.left + alvo.width + 16 };
    }
    if (alvo.top + alvo.height + 230 < vh) return { top: alvo.top + alvo.height + 14, left: cabe(alvo.left + alvo.width - largura) };
    return { top: Math.max(16, alvo.top - 250), left: cabe(alvo.left + alvo.width - largura) };
}

export default function TourGuiado({ passos, onFechar }: { passos: PassoTour[]; onFechar: (concluido: boolean) => void }) {
    // Só os passos com alvo na tela agora (ex.: Indicadores some para quem não é autoridade).
    const validos = useMemo(() => passos.filter((p) => !p.alvo || alvoVisivel(p.alvo)), [passos]);
    const [idx, setIdx] = useState(0);
    const [caixa, setCaixa] = useState<Caixa | null>(null);
    const passo = validos[idx];
    const ultimo = idx === validos.length - 1;

    const medir = useCallback(() => {
        if (!passo?.alvo) {
            setCaixa(null);
            return;
        }
        const el = alvoVisivel(passo.alvo);
        if (!el) {
            setCaixa(null);
            return;
        }
        const r = el.getBoundingClientRect();
        setCaixa({ top: r.top - FOLGA / 2, left: r.left - FOLGA / 2, width: r.width + FOLGA, height: r.height + FOLGA });
    }, [passo]);

    useLayoutEffect(() => {
        if (passo?.alvo) {
            const el = alvoVisivel(passo.alvo);
            const r = el?.getBoundingClientRect();
            if (el && r && (r.top < 70 || r.bottom > window.innerHeight - 20)) el.scrollIntoView({ block: 'center' });
        }
        medir();
    }, [medir, passo]);

    useEffect(() => {
        window.addEventListener('resize', medir);
        window.addEventListener('scroll', medir, true);
        return () => {
            window.removeEventListener('resize', medir);
            window.removeEventListener('scroll', medir, true);
        };
    }, [medir]);

    const avancar = useCallback(() => (ultimo ? onFechar(true) : setIdx((i) => i + 1)), [onFechar, ultimo]);
    const voltar = useCallback(() => setIdx((i) => Math.max(0, i - 1)), []);

    useEffect(() => {
        const tecla = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onFechar(false);
            else if (e.key === 'ArrowRight' || e.key === 'Enter') avancar();
            else if (e.key === 'ArrowLeft') voltar();
        };
        window.addEventListener('keydown', tecla);
        return () => window.removeEventListener('keydown', tecla);
    }, [avancar, onFechar, voltar]);

    if (!passo) return null;
    const pos = posicaoCartao(caixa);

    return createPortal(
        <div className="tg-root tg-tour" data-testid="trial-tour">
            {caixa ? (
                <div className="tg-tour-luz" style={{ top: caixa.top, left: caixa.left, width: caixa.width, height: caixa.height }} aria-hidden="true" />
            ) : (
                <div className="tg-tour-veu" aria-hidden="true" />
            )}
            <section
                key={idx}
                className="tg-tour-cartao"
                style={{ top: pos.top, left: pos.left, width: Math.min(CARTAO_LARGURA, window.innerWidth - 32) }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="tg-tour-titulo"
            >
                <div className="tg-tour-topo">
                    <span className="tg-tour-marca"><CeleriIcone className="h-4 w-4" /></span>
                    <span className="tg-tour-passo">Passo {idx + 1} de {validos.length}</span>
                    <button type="button" className="tg-tour-pular" onClick={() => onFechar(false)}>Pular tour</button>
                </div>
                <h3 id="tg-tour-titulo">{passo.titulo}</h3>
                <p>{passo.texto}</p>
                <div className="tg-tour-pontos" aria-hidden="true">
                    {validos.map((_, i) => <i key={i} className={i === idx ? 'is-atual' : i < idx ? 'is-feito' : ''} />)}
                </div>
                <div className="tg-tour-acoes">
                    {idx > 0 ? (
                        <button type="button" className="tg-btn tg-btn-secundario" onClick={voltar}>
                            <Lucide icon="ArrowLeft" className="h-4 w-4" /> Voltar
                        </button>
                    ) : <span />}
                    <button type="button" className="tg-btn tg-btn-primario" onClick={avancar} data-testid="trial-tour-next">
                        {ultimo ? 'Concluir' : 'Próximo'} <Lucide icon={ultimo ? 'Check' : 'ArrowRight'} className="h-4 w-4" />
                    </button>
                </div>
            </section>
        </div>,
        document.body,
    );
}
